import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  extractResumeSection,
  enhanceResumeSection,
  extractAllSections,
  enhanceAllSections,
  saveProfessionalSummary,
  saveTechnicalSkills,
  saveWorkExperience,
  saveProjectExperience,
  saveEducation,
  saveLanguages,
  saveCertifications,
  saveAchievements,
  getResumeData,
  getResumeSectionData,
  getJobs,
} from '../api';
import ProfessionalSummaryModal from './ProfessionalSummaryModal';
import TechnicalSkillsModal from './TechnicalSkillsModal';
import ProjectExperienceModal from './ProjectExperienceModal';
import WorkExperienceModal from './WorkExperienceModal';
import EducationModal from './EducationModal';
import CertificationModal from './CertificationModal';
import AchievementModal from './AchievementModal';

const sectionTitles = {
  professionalSummary: 'Professional Summary',
  technicalSkills: 'Technical Skills',
  workExperience: 'Work Experience',
  projectExperience: 'Project Experience',
  education: 'Education',
  languages: 'Languages',
  certifications: 'Certifications',
  achievements: 'Achievements',
};

const sectionKeyToServerKey = {
  professionalSummary: 'summary',
  technicalSkills: 'skills',
  workExperience: 'workexp',
  projectExperience: 'projects',
  achievements: 'certifications',
};

const serverKeyToClientKeys = {
  summary: ['professionalSummary'],
  skills: ['technicalSkills'],
  workexp: ['workExperience'],
  projects: ['projectExperience'],
  certifications: ['certifications', 'achievements'],
  education: ['education'],
  languages: ['languages'],
};

const mapClientToServerSection = (sectionKey) => sectionKeyToServerKey[sectionKey] || sectionKey;
const mapServerToClientSectionKeys = (sectionKey) => serverKeyToClientKeys[sectionKey] || [sectionKey];

export const parseProjectExperienceText = (rawText = '') => {
  const normalizedText = (rawText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalizedText) {
    return [];
  }

  const textWithoutHeader = normalizedText.replace(/^PROJECT\s+EXPERIENCE\s*:\s*/i, '').trim();

  const projectBlocks = [...textWithoutHeader.matchAll(/(?:^|\n)\s*Project:\s*([^\n]+)\n([\s\S]*?)(?=(?:\n\s*Project:\s*)|\s*$)/gi)];

  if (projectBlocks.length > 0) {
    return projectBlocks
      .map(([, projectName, blockText]) => {
        const cleanedProjectName = (projectName || '').replace(/\s+/g, ' ').trim();
        const bulletLines = [...(blockText || '').matchAll(/^\s*[•\-\*]\s*(.+)$/gm)]
          .map((match) => match[1].trim())
          .filter(Boolean);

        const details = bulletLines.length
          ? bulletLines.join('\n')
          : (blockText || '')
              .replace(/^(?:Environment|Description|Responsibilities|Key Achievement)\s*:\s*/gim, '')
              .replace(/\n{3,}/g, '\n\n')
              .trim();

        return {
          projectName: cleanedProjectName,
          startDate: '',
          endDate: '',
          details: details || '',
        };
      })
      .filter((project) => project.projectName || project.details);
  }

  const bulletLines = [...textWithoutHeader.matchAll(/^\s*[•\-\*]\s*(.+)$/gm)]
    .map((match) => match[1].trim())
    .filter(Boolean);

  if (bulletLines.length > 0) {
    return [{ projectName: '', startDate: '', endDate: '', details: bulletLines.join('\n') }];
  }

  return [{ projectName: '', startDate: '', endDate: '', details: textWithoutHeader || normalizedText }];
};

const monthMap = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

const isEducationKeywordMatch = (value = '') => /(bachelor|master|diploma|certificate|degree|mca|bca|bcom|bsc|ba|msc|mtech|btech|be|phd|graduation|postgraduate|undergraduate|course|education)/i.test(value || '');

const isInstitutionKeywordMatch = (value = '') => /(university|college|institute|school|academy|polytechnic|technology|campus|board)/i.test(value || '');

const isLikelyEducationEntry = ({ degree = '', university = '', startDate = '', endDate = '' }) => {
  const degreeText = (degree || '').trim();
  const universityText = (university || '').trim();
  const hasDate = Boolean(startDate || endDate);

  if (!degreeText && !universityText && !hasDate) {
    return false;
  }

  const hasEducationSignal = isEducationKeywordMatch(degreeText) || isInstitutionKeywordMatch(universityText);
  if (hasEducationSignal) {
    return true;
  }

  return Boolean(hasDate && (degreeText || universityText));
};

const normalizeEducationDateValue = (dateText = '') => {
  if (!dateText) return '';

  const cleaned = String(dateText).trim().replace(/\s+/g, ' ');
  const monthMatch = cleaned.match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{4})$/i);
  if (monthMatch) {
    const month = monthMap[monthMatch[1].toLowerCase()];
    return `${monthMatch[2]}-${String(month).padStart(2, '0')}-01`;
  }

  const yearMatch = cleaned.match(/^(\d{4})$/);
  if (yearMatch) {
    return `${yearMatch[1]}-01-01`;
  }

  const dashMatch = cleaned.match(/^(\d{4})[-/](\d{1,2})$/);
  if (dashMatch) {
    return `${dashMatch[1]}-${String(dashMatch[2]).padStart(2, '0')}-01`;
  }

  const rangeMatch = cleaned.match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{4})\s*(?:-|–|to)\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{4})$/i);
  if (rangeMatch) {
    const start = `${rangeMatch[2]}-${String(monthMap[rangeMatch[1].toLowerCase()]).padStart(2, '0')}-01`;
    const end = `${rangeMatch[4]}-${String(monthMap[rangeMatch[3].toLowerCase()]).padStart(2, '0')}-01`;
    return { startDate: start, endDate: end };
  }

  return cleaned;
};

export const parseEducationText = (rawText = '') => {
  const normalizedText = (rawText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalizedText) return [];

  let text = normalizedText.replace(/^EDUCATION\s*:\s*/i, '').trim();
  if (!text) {
    return [];
  }

  const blocks = text
    .split(/\n\s*\n+/)
    .map((block) => block.trim())
    .filter(Boolean);

  const entries = [];

  blocks.forEach((block) => {
    const lines = block.split(/\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length === 0) return;

    const firstLine = lines[0] || '';
    const secondLine = lines[1] || '';

    let degree = firstLine;
    let university = '';
    let dateText = '';

    if (secondLine) {
      const secondLineClean = secondLine.replace(/\s*\|\s*/g, ' | ');
      const pipeMatch = secondLineClean.match(/^(.*?)(?:\s*\|\s*)(.*)$/);
      if (pipeMatch) {
        university = pipeMatch[1].trim();
        dateText = pipeMatch[2].trim();
      } else {
        const dateMatch = secondLineClean.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{4}\s*(?:-|–|to)\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{4}/i);
        if (dateMatch) {
          const beforeDate = secondLineClean.slice(0, secondLineClean.indexOf(dateMatch[0])).trim();
          university = beforeDate.replace(/[|,]+$/, '').trim();
          dateText = dateMatch[0];
        } else {
          university = secondLineClean;
        }
      }
    }

    if (!degree || degree.toLowerCase().startsWith('education')) {
      degree = lines[0] || '';
    }

    if (!university && lines.length > 1) {
      const fallback = lines.slice(1).join(' ');
      const fallbackPipe = fallback.match(/^(.*?)(?:\s*\|\s*)(.*)$/);
      if (fallbackPipe) {
        university = fallbackPipe[1].trim();
        dateText = fallbackPipe[2].trim();
      }
    }

    if (!university && lines.length > 1) {
      const joined = lines.slice(1).join(' ');
      const cleanJoined = joined.replace(/\s*\|\s*/g, ' | ');
      const pipeMatch = cleanJoined.match(/^(.*?)(?:\s*\|\s*)(.*)$/);
      if (pipeMatch) {
        university = pipeMatch[1].trim();
        dateText = pipeMatch[2].trim();
      }
    }

    const degreeTrimmed = degree.replace(/^(?:Degree|Education)\s*[:\-]?\s*/i, '').trim();
    const universityTrimmed = university.replace(/[|,]+$/, '').trim();

    const dates = dateText ? dateText.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{4}\s*(?:-|–|to)\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{4}/i) : null;

    let startDate = '';
    let endDate = '';
    if (dates) {
      const normalizedDates = normalizeEducationDateValue(dates[0]);
      if (normalizedDates && typeof normalizedDates === 'object') {
        startDate = normalizedDates.startDate;
        endDate = normalizedDates.endDate;
      }
    }

    if (!degreeTrimmed && !universityTrimmed && !dateText) {
      return;
    }

    const nextEntry = {
      degree: degreeTrimmed,
      university: universityTrimmed,
      grade: '',
      startDate,
      endDate,
    };

    if (isLikelyEducationEntry(nextEntry)) {
      entries.push(nextEntry);
    }
  });

  if (entries.length > 0) return entries;

  const singleLine = text;
  const singleDegreeMatch = singleLine.match(/^(.+?)(?:\s*\|\s*)(.+)$/);
  if (singleDegreeMatch) {
    const degree = singleDegreeMatch[1].trim();
    const rest = singleDegreeMatch[2].trim();
    const dateMatch = rest.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{4}\s*(?:-|–|to)\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{4}/i);
    let university = rest;
    let startDate = '';
    let endDate = '';
    if (dateMatch) {
      university = rest.slice(0, rest.indexOf(dateMatch[0])).trim().replace(/[|,]+$/, '');
      const normalizedDates = normalizeEducationDateValue(dateMatch[0]);
      if (normalizedDates && typeof normalizedDates === 'object') {
        startDate = normalizedDates.startDate;
        endDate = normalizedDates.endDate;
      }
    }

    const singleEntry = { degree, university, grade: '', startDate, endDate };
    if (isLikelyEducationEntry(singleEntry)) {
      return [singleEntry];
    }
  }

  return [];
};

export const normalizePdfDetailLines = (details = '') => {
  const text = String(details ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!text) return '';

  const lines = text
    .split(/\n+/)
    .map((line) => line.replace(/^[-•*]\s*/, '').trim())
    .filter(Boolean);

  if (lines.length === 0) return '';
  return lines.map((line) => `• ${line}`).join('\n');
};

export const buildResumePdfSections = ({
  formData = {},
  technicalSections = [],
  workExperiences = [],
  projectEntries = [],
  educationEntries = [],
  languagesText = '',
  certificationEntries = [],
  achievementEntries = [],
} = {}) => {
  const toText = (value) => typeof value === 'string' ? value.trim() : (value ?? '');

  const technicalContent = technicalSections
    .map((section) => {
      const title = toText(section.title);
      const details = toText(section.details).replace(/\n+/g, ' ');
      if (!title && !details) return '';
      return title ? `${title}: ${details}`.trim() : details;
    })
    .filter(Boolean)
    .join('\n');

  const workContent = workExperiences
    .map((entry) => {
      const lines = [
        entry.company ? `Company: ${entry.company}` : '',
        entry.jobTitle ? `Role: ${entry.jobTitle}` : '',
        entry.startDate || entry.endDate ? `Period: ${entry.startDate || '—'} to ${entry.endDate || 'Present'}` : '',
        entry.details ? normalizePdfDetailLines(entry.details) : '',
      ].filter(Boolean);
      return lines.join('\n');
    })
    .filter(Boolean)
    .join('\n\n');

  const projectContent = projectEntries
    .map((entry) => {
      const lines = [
        entry.projectName ? `Project: ${entry.projectName}` : '',
        entry.startDate || entry.endDate ? `Period: ${entry.startDate || '—'} to ${entry.endDate || 'Present'}` : '',
        entry.details ? normalizePdfDetailLines(entry.details) : '',
      ].filter(Boolean);
      return lines.join('\n');
    })
    .filter(Boolean)
    .join('\n\n');

  const educationContent = educationEntries
    .map((entry) => {
      const lines = [
        entry.degree ? `Degree: ${entry.degree}` : '',
        entry.university ? `University: ${entry.university}` : '',
        entry.grade ? `Grade: ${entry.grade}` : '',
        entry.startDate || entry.endDate ? `Period: ${entry.startDate || '—'} to ${entry.endDate || 'Present'}` : '',
      ].filter(Boolean);
      return lines.join('\n');
    })
    .filter(Boolean)
    .join('\n\n');

  const certificationContent = certificationEntries
    .map((entry) => {
      const lines = [
        entry.name ? `Certification: ${entry.name}` : '',
        entry.issuer ? `Issuer: ${entry.issuer}` : '',
        entry.startDate || entry.endDate ? `Period: ${entry.startDate || '—'} to ${entry.endDate || 'Present'}` : '',
        entry.details ? normalizePdfDetailLines(entry.details) : '',
      ].filter(Boolean);
      return lines.join('\n');
    })
    .filter(Boolean)
    .join('\n\n');

  const achievementContent = achievementEntries
    .map((entry) => {
      const lines = [
        entry.name ? `Achievement: ${entry.name}` : '',
        entry.issuer ? `Issuer: ${entry.issuer}` : '',
        entry.startDate || entry.endDate ? `Period: ${entry.startDate || '—'} to ${entry.endDate || 'Present'}` : '',
        entry.details ? normalizePdfDetailLines(entry.details) : '',
      ].filter(Boolean);
      return lines.join('\n');
    })
    .filter(Boolean)
    .join('\n\n');

  const sections = [
    { title: 'Professional Summary', content: toText(formData.professionalSummary) },
    { title: 'Technical Skills', content: technicalContent },
    { title: 'Work Experience', content: workContent },
    { title: 'Project Experience', content: projectContent },
    { title: 'Education', content: educationContent },
    { title: 'Languages', content: toText(languagesText) },
    { title: 'Certifications', content: certificationContent },
    { title: 'Achievements', content: achievementContent },
  ];

  return sections.filter((section) => String(section.content || '').trim());
};

export const parseTechnicalSkillsText = (rawText = '') => {
  const normalizedText = (rawText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalizedText) return [];

  // Remove header like "TECHNICAL SKILLS:" if present
  const text = normalizedText.replace(/^TECHNICAL\s+SKILLS\s*:\s*/i, '').trim();

  const entries = [];

  // Match lines that look like bullets with a category: e.g. "• Data Engineering: Databricks..."
  const lineRegex = /^\s*[•\-\*]?\s*([^:\n]+):\s*(.+)$/gm;
  let m;
  while ((m = lineRegex.exec(text)) !== null) {
    const title = (m[1] || '').trim();
    let details = (m[2] || '').trim();

    // Capture any following lines that are indented or comma/continuation until next category
    const restStart = m.index + m[0].length;
    const nextMatch = (() => {
      const next = lineRegex.exec(text);
      if (next) return next.index;
      return text.length;
    })();
    // Reset regex lastIndex to continue from current position for next loop
    lineRegex.lastIndex = restStart;

    // Pull the continuation segment between restStart and nextMatch
    const continuation = text.slice(restStart, nextMatch).trim();
    if (continuation) {
      // append continuation lines to details (clean leading bullets/spaces)
      const contClean = continuation.replace(/^\s*[•\-\*]?\s*/gm, '\n').trim();
      if (contClean) details = details + '\n' + contClean;
    }

    entries.push({ title, details });
    // position regex for next iteration already set
  }

  if (entries.length > 0) return entries;

  // Fallback: try to parse simple bullet list into a single unnamed section
  const bullets = [...text.matchAll(/^\s*[•\-\*]\s*(.+)$/gm)].map((r) => r[1].trim()).filter(Boolean);
  if (bullets.length > 0) return [{ title: '', details: bullets.join('\n') }];

  // Last fallback: return the entire block as one unnamed section
  return [{ title: '', details: text }];
};

export const sanitizeProfessionalSummaryText = (rawText = '') => {
  const text = String(rawText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!text) return '';

  const cleanSection = text
    .replace(/^\s*PROFESSIONAL\s+SUMMARY\s*:\s*/i, '')
    .replace(/^\s*SUMMARY\s*:\s*/i, '')
    .replace(/^\s*HEMANGI\s+WAGH\s*$/gim, '')
    .replace(/^\s*Full-Stack\s+Software\s+Developer\s*\|?.*$/gim, '')
    .replace(/^\s*[+\d\s\-()]+\s*\|\s*.*@.*\|\s*.*$/gim, '')
    .replace(/^\s*Pune,\s*Maharashtra\s*$/gim, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s*\n\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.])/g, '$1')
    .trim();

  return cleanSection;
};

export const parseWorkExperienceText = (rawText = '') => {
  const normalizedText = (rawText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalizedText) return [];

  // Remove header if present
  const text = normalizedText.replace(/^WORK\s+EXPERIENCE\s*:\s*/i, '').trim();

  // Try to split by repeated 'Company:' blocks
  const companyBlocks = [...text.matchAll(/(?:^|\n)\s*(?:Company|Employer|Organization)\s*:\s*([^\n]+)\n([\s\S]*?)(?=(?:\n\s*(?:Company|Employer|Organization)\s*:)|\s*$)/gi)];
  if (companyBlocks.length > 0) {
    return companyBlocks.map(([, companyName, blockText]) => {
      const company = (companyName || '').trim();
      // Extract title from 'Title:' or 'Role:' lines or first '|' pattern
      let title = '';
      const titleMatch = blockText.match(/(?:Title|Role)\s*:\s*([^\n]+)/i);
      if (titleMatch) title = titleMatch[1].trim();

      // Dates: try to find start/end on lines or in a pipe-separated header
      let startDate = '';
      let endDate = '';
      const datesMatch = blockText.match(/(\d{4}(?:[-/]\d{1,2}(?:[-/]\d{1,2})?)?)\s*(?:-|to|–)\s*(\d{4}|Present|present|\d{4}(?:[-/]\d{1,2}(?:[-/]\d{1,2})?))/);
      if (datesMatch) {
        startDate = datesMatch[1];
        endDate = datesMatch[2];
      }

      // Bullet details
      const bullets = [...(blockText || '').matchAll(/^\s*[•\-\*]\s*(.+)$/gm)].map((m) => m[1].trim()).filter(Boolean);
      const details = bullets.length ? bullets.join('\n') : blockText.replace(/^(?:Title|Role|Start Date|End Date):.*$/gim, '').trim();

      return { company, jobTitle: title, startDate, endDate, details };
    }).filter((w) => w.company || w.details);
  }

  // Try splitting by blank line groups that contain a pipe-separated header like: Company | Title | dates
  const groups = text.split(/\n{2,}/).map((g) => g.trim()).filter(Boolean);
  const parsed = groups.map((g) => {
    const headerMatch = g.match(/^([^\|\n]+)\s*\|\s*([^\|\n]+)\s*\|\s*([^\n]+)/);
    if (headerMatch) {
      const company = headerMatch[1].trim();
      const jobTitle = headerMatch[2].trim();
      const dates = headerMatch[3].trim();
      let startDate = '';
      let endDate = '';
      const dmatch = dates.match(/(\d{4}(?:[-/]\d{1,2}(?:[-/]\d{1,2})?)?)\s*(?:-|to|–)\s*(\d{4}|Present|present|\d{4}(?:[-/]\d{1,2}(?:[-/]\d{1,2})?))/);
      if (dmatch) { startDate = dmatch[1]; endDate = dmatch[2]; }
      const bullets = [...g.matchAll(/^\s*[•\-\*]\s*(.+)$/gm)].map((m) => m[1].trim()).filter(Boolean);
      const details = bullets.length ? bullets.join('\n') : g.replace(headerMatch[0], '').trim();
      return { company, jobTitle, startDate, endDate, details };
    }
    // fallback: try to find first line as company/title
    const firstLine = g.split('\n')[0];
    const parts = firstLine.split('|').map((p) => p.trim());
    if (parts.length >= 2) {
      const company = parts[0];
      const jobTitle = parts[1];
      const dates = parts[2] || '';
      let startDate = '';
      let endDate = '';
      const dmatch = dates.match(/(\d{4}(?:[-/]\d{1,2}(?:[-/]\d{1,2})?)?)\s*(?:-|to|–)\s*(\d{4}|Present|present|\d{4}(?:[-/]\d{1,2}(?:[-/]\d{1,2})?))/);
      if (dmatch) { startDate = dmatch[1]; endDate = dmatch[2]; }
      const bullets = [...g.matchAll(/^\s*[•\-\*]\s*(.+)$/gm)].map((m) => m[1].trim()).filter(Boolean);
      const details = bullets.length ? bullets.join('\n') : g.split('\n').slice(1).join('\n').trim();
      return { company, jobTitle, startDate, endDate, details };
    }
    const bullets = [...g.matchAll(/^\s*[•\-\*]\s*(.+)$/gm)].map((m) => m[1].trim()).filter(Boolean);
    return { company: '', jobTitle: '', startDate: '', endDate: '', details: bullets.length ? bullets.join('\n') : g };
  }).filter(Boolean);

  if (parsed.length > 0) return parsed;

  // Fallback: single entry with whole text
  return [{ company: '', jobTitle: '', startDate: '', endDate: '', details: text }];
};

const ResumeDataInsertPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [resumeUploaded, setResumeUploaded] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [fileError, setFileError] = useState('');
  // server-side GROQ key is used; client override removed
  const [jobDescription, setJobDescription] = useState('');
  const [jobDescriptionType, setJobDescriptionType] = useState('Full JD');
  const [selectedJdId, setSelectedJdId] = useState('');
  const [customJobDescription, setCustomJobDescription] = useState('');
  const [jobOptions, setJobOptions] = useState([]);
  const [jobOptionsLoading, setJobOptionsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [isBulkExtracting, setIsBulkExtracting] = useState(false);
  const [sectionLoading, setSectionLoading] = useState({});
  const [sectionErrors, setSectionErrors] = useState({});
  const [sectionStructured, setSectionStructured] = useState({});
  const [formData, setFormData] = useState({ professionalSummary: '' });
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isTechModalOpen, setIsTechModalOpen] = useState(false);
  const [editingTechIndex, setEditingTechIndex] = useState(null);
  const [isEducationModalOpen, setIsEducationModalOpen] = useState(false);
  const [editingEducationIndex, setEditingEducationIndex] = useState(null);
  const [isCertificationModalOpen, setIsCertificationModalOpen] = useState(false);
  const [editingCertificationIndex, setEditingCertificationIndex] = useState(null);
  const [isAchievementModalOpen, setIsAchievementModalOpen] = useState(false);
  const [editingAchievementIndex, setEditingAchievementIndex] = useState(null);
  const [candidateId, setCandidateId] = useState('');
  const [resumeId, setResumeId] = useState('');

  const routeCandidateId = location.state?.candidateId ?? location.state?.candidateData?.id ?? location.state?.candidateData?.CandidateID ?? '';
  const routeResumeId = location.state?.resumeId ?? location.state?.candidateData?.resumeId ?? location.state?.candidateData?.ResumeID ?? '';

  useEffect(() => {
    const nextCandidateId = routeCandidateId;
    const nextResumeId = routeResumeId;

    console.log('[UI][resume-update] route state received', {
      pathname: location.pathname,
      routeCandidateId: nextCandidateId,
      routeResumeId: nextResumeId,
      state: location.state,
    });

    if (nextCandidateId !== '' || nextResumeId !== '') {
      setCandidateId(String(nextCandidateId || ''));
      setResumeId(String(nextResumeId || ''));
    }
  }, [routeCandidateId, routeResumeId, location.pathname, location.state]);

  const hydrateResumeDraftData = (draft) => {
    if (!draft || typeof draft !== 'object') {
      return;
    }

    const safeDraft = draft.resumeData && typeof draft.resumeData === 'object' ? draft.resumeData : draft;

    if (typeof safeDraft.professionalSummary === 'string') {
      setFormData((prev) => ({ ...prev, professionalSummary: safeDraft.professionalSummary || '' }));
    }

    const technicalSource = Array.isArray(safeDraft.technicalSkills)
      ? safeDraft.technicalSkills
      : Array.isArray(safeDraft.technicalSections)
        ? safeDraft.technicalSections
        : [];

    if (technicalSource.length > 0) {
      setTechnicalSections(technicalSource.map((item) => ({
        title: item?.title || item?.label || item?.section || '',
        details: Array.isArray(item?.details) ? item.details.join('\n') : (item?.details || item?.text || item?.value || ''),
      })));
    } else if (typeof safeDraft.technicalSkills === 'string' && safeDraft.technicalSkills.trim()) {
      setTechnicalSections([{ title: '', details: safeDraft.technicalSkills }]);
    }

    const workSource = Array.isArray(safeDraft.workExperience)
      ? safeDraft.workExperience
      : Array.isArray(safeDraft.workExperiences)
        ? safeDraft.workExperiences
        : [];

    if (workSource.length > 0) {
      setWorkExperiences(workSource.map((item) => ({
        company: item?.company || item?.organization || '',
        jobTitle: item?.jobTitle || item?.title || '',
        startDate: item?.startDate || '',
        endDate: item?.endDate || '',
        details: Array.isArray(item?.details) ? item.details.join('\n') : (item?.details || item?.description || ''),
      })));
    }

    const projectSource = Array.isArray(safeDraft.projectExperience)
      ? safeDraft.projectExperience
      : Array.isArray(safeDraft.projects)
        ? safeDraft.projects
        : [];

    if (projectSource.length > 0) {
      setProjectEntries(projectSource.map((item) => ({
        projectName: item?.projectName || item?.name || '',
        startDate: item?.startDate || '',
        endDate: item?.endDate || '',
        details: Array.isArray(item?.details) ? item.details.join('\n') : (item?.details || item?.description || ''),
      })));
    }

    const educationSource = Array.isArray(safeDraft.education)
      ? safeDraft.education
      : Array.isArray(safeDraft.educationEntries)
        ? safeDraft.educationEntries
        : [];

    if (educationSource.length > 0) {
      setEducationEntries(educationSource.map((item) => ({
        degree: item?.degree || item?.name || '',
        university: item?.university || item?.institution || '',
        grade: item?.grade || item?.cgpa || item?.gpa || '',
        startDate: item?.startDate || '',
        endDate: item?.endDate || '',
      })));
    }

    if (typeof safeDraft.languages === 'string' && safeDraft.languages.trim()) {
      setLanguagesText(safeDraft.languages);
    } else if (Array.isArray(safeDraft.languages)) {
      setLanguagesText(safeDraft.languages.join(', '));
    }

    const certificationsSource = Array.isArray(safeDraft.certifications)
      ? safeDraft.certifications
      : Array.isArray(safeDraft.certificationEntries)
        ? safeDraft.certificationEntries
        : [];

    if (certificationsSource.length > 0) {
      setCertificationEntries(certificationsSource.map((item) => ({
        name: item?.name || item?.title || '',
        issuer: item?.issuer || '',
        startDate: item?.startDate || '',
        endDate: item?.endDate || '',
        details: Array.isArray(item?.details) ? item.details.join('\n') : (item?.details || item?.description || ''),
      })));
    }

    const achievementsSource = Array.isArray(safeDraft.achievements)
      ? safeDraft.achievements
      : Array.isArray(safeDraft.achievementEntries)
        ? safeDraft.achievementEntries
        : [];

    if (achievementsSource.length > 0) {
      setAchievementEntries(achievementsSource.map((item) => ({
        name: item?.name || item?.title || '',
        issuer: item?.issuer || '',
        startDate: item?.startDate || '',
        endDate: item?.endDate || '',
        details: Array.isArray(item?.details) ? item.details.join('\n') : (item?.details || item?.description || ''),
      })));
    }
  };

  const buildFullJobDescription = (job = {}) => {
    const sections = [];

    if (job.title) sections.push(`Title: ${job.title}`);
    if (job.company) sections.push(`Company: ${job.company}`);
    if (job.department) sections.push(`Department: ${job.department}`);
    if (job.location) sections.push(`Location: ${job.location}`);
    if (job.job_type) sections.push(`Job Type: ${job.job_type}`);
    if (job.status) sections.push(`Status: ${job.status}`);
    if (job.experience_required) sections.push(`Experience Required: ${job.experience_required}`);
    if (job.skill) sections.push(`Skills: ${job.skill}`);
    if (job.budget) sections.push(`Budget: ${job.budget}`);
    if (job.job_description) sections.push(`Job Description:\n${job.job_description}`);

    return sections.join('\n\n').trim();
  };

  const fetchJobOptions = async () => {
    try {
      setJobOptionsLoading(true);
      const response = await getJobs();
      if (response?.success) {
        setJobOptions(Array.isArray(response.data) ? response.data : []);
      } else {
        console.warn('[UI][resume-update] Failed to load JD list', response);
      }
    } catch (error) {
      console.error('[UI][resume-update] Failed to load JD list:', error);
    } finally {
      setJobOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobOptions();
  }, []);

  useEffect(() => {
    const loadSavedResumeData = async () => {
      const effectiveResumeId = Number(routeResumeId || resumeId || '');
      const effectiveCandidateId = routeCandidateId || candidateId;

      console.log('[UI][resume-update] fetching saved resume data', {
        routeResumeId,
        currentResumeId: resumeId,
        effectiveResumeId,
        routeCandidateId,
        currentCandidateId: candidateId,
        effectiveCandidateId,
        pathname: location.pathname,
      });

      if (!effectiveResumeId) {
        console.warn('[UI][resume-update] skip fetch because effectiveResumeId is missing', {
          routeResumeId,
          resumeId,
          routeCandidateId,
          candidateId,
        });
        return;
      }

      try {
        setStatusType('info');
        setStatusMessage('Loading saved resume data...');

        const sectionNames = [
          'professionalSummary',
          'technicalSkills',
          'workExperience',
          'projectExperience',
          'education',
          'languages',
          'certifications',
          'achievements',
        ];

        const sectionMap = {
          professionalSummary: 'summary',
          technicalSkills: 'skills',
          workExperience: 'work-experience',
          projectExperience: 'projects',
          education: 'education',
          languages: 'languages',
          certifications: 'certifications',
          achievements: 'achievements',
        };

        const hasValue = (value) => {
          if (value === null || value === undefined) return false;
          if (typeof value === 'string') return value.trim().length > 0;
          if (Array.isArray(value)) return value.length > 0;
          if (typeof value === 'object') return Object.keys(value).length > 0;
          return Boolean(value);
        };

        const results = await Promise.allSettled(
          sectionNames.map((sectionName) => getResumeSectionData(effectiveResumeId, sectionMap[sectionName]))
        );

        const mergedDraft = {};
        for (let index = 0; index < results.length; index += 1) {
          const result = results[index];
          if (result.status !== 'fulfilled') continue;

          const responseData = result.value?.data ?? result.value?.data?.data ?? null;
          const sectionValue = responseData?.value ?? null;
          const sectionName = sectionNames[index];

          console.log('[UI][resume-update] section result', {
            sectionName,
            sectionKey: sectionMap[sectionName],
            hasValue: hasValue(sectionValue),
            sectionValue,
          });

          if (hasValue(sectionValue)) {
            mergedDraft[sectionName] = sectionValue;
          }
        }

        console.log('[UI][resume-update] merged draft before hydrate', {
          effectiveResumeId,
          mergedKeys: Object.keys(mergedDraft),
          mergedDraft,
        });

        if (!Object.keys(mergedDraft).length) {
          setStatusMessage('No data found for this resume.');
          setStatusType('info');
          return;
        }

        hydrateResumeDraftData(mergedDraft);
        setResumeUploaded(true);
        setStatusMessage('Saved resume data loaded successfully.');
        setStatusType('success');
      } catch (error) {
        console.error('[UI][resume-update] Failed to load saved resume data:', error);
        setStatusMessage(error?.message || 'Unable to load saved resume data.');
        setStatusType('error');
      }
    };

    loadSavedResumeData();
  }, [routeResumeId, resumeId, routeCandidateId, candidateId, location.pathname]);

  const pdfPreviewRef = useRef(null);
  const [technicalSections, setTechnicalSections] = useState([{ title: '', details: '' }]);
  const [workExperiences, setWorkExperiences] = useState([{ company: '', jobTitle: '', startDate: '', endDate: '', details: '' }]);
  const [projectEntries, setProjectEntries] = useState([{ projectName: '', startDate: '', endDate: '', details: '' }]);
  const [educationEntries, setEducationEntries] = useState([{ degree: '', university: '', grade: '', startDate: '', endDate: '' }]);
  const [languagesText, setLanguagesText] = useState('');
  const [certificationEntries, setCertificationEntries] = useState([{ name: '', issuer: '', startDate: '', endDate: '', details: '' }]);
  const [achievementEntries, setAchievementEntries] = useState([{ name: '', issuer: '', startDate: '', endDate: '', details: '' }]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setResumeUploaded(false);
      setSelectedFile(null);
      setStatusMessage('No file selected.');
      setStatusType('error');
      return;
    }

    console.log('UI: handleFileChange - selected file', { name: file.name, size: file.size, type: file.type });

    setSelectedFile(file);
    setResumeUploaded(true);
    setStatusMessage('Resume uploaded.');
    setStatusType('success');

    // Auto-start extraction using server-side GROQ key (from API .env). Optional client key is supported.
    setStatusMessage('Resume uploaded. Extracting resume sections now...');
    handleExtractAllSections(file);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckJobDescription = () => {
    const trimmedJD = (jobDescription || '').trim();
    if (!trimmedJD) {
      setStatusMessage('Please select a JD or enter a custom job description before checking it.');
      setStatusType('error');
      return;
    }

    const preview = trimmedJD.length > 160 ? `${trimmedJD.slice(0, 160)}...` : trimmedJD;
    setStatusMessage(`JD checked successfully (${jobDescriptionType || 'Full JD'}). Selected value: ${preview}`);
    setStatusType('info');
  };

  const handleJdSelectionChange = (event) => {
    const nextValue = event.target.value;

    if (!nextValue) {
      setSelectedJdId('');
      setJobDescription(customJobDescription.trim());
      return;
    }

    const selectedJob = jobOptions.find((job) => String(job.job_id) === String(nextValue));
    setSelectedJdId(String(nextValue));

    if (selectedJob) {
      const fullJD = buildFullJobDescription(selectedJob);
      setJobDescription(fullJD);
      setCustomJobDescription('');
      return;
    }

    setJobDescription('');
  };

  const handleCustomJdChange = (event) => {
    const nextValue = event.target.value;
    setCustomJobDescription(nextValue);
    const trimmedValue = nextValue.trim();
    setJobDescription(trimmedValue);
    setSelectedJdId(trimmedValue ? 'custom' : '');
  };

  const handleSaveProfessionalSummary = async () => {
    const summary = (formData.professionalSummary || '').trim();
    console.log('[UI] handleSaveProfessionalSummary clicked:', {
      candidateId,
      resumeId,
      summaryLength: summary.length,
      summaryPreview: summary.slice(0, 200),
    });

    if (!summary) {
      setStatusMessage('Professional summary is required before saving.');
      setStatusType('error');
      return false;
    }

    if (!candidateId && !resumeId) {
      setStatusMessage('Enter a Candidate ID or Resume ID before saving the professional summary.');
      setStatusType('error');
      return false;
    }

    try {
      setStatusMessage('Saving professional summary...');
      setStatusType('info');
      const payload = {
        candidateId: candidateId ? Number(candidateId) : null,
        resumeId: resumeId ? Number(resumeId) : null,
        professionalSummary: summary,
      };
      console.log('[UI] Professional summary payload to API:', JSON.stringify(payload, null, 2));

      const result = await saveProfessionalSummary(payload);
      console.log('[UI] Professional summary save result:', JSON.stringify(result, null, 2));

      if (!result?.success) {
        throw new Error(result?.message || 'Unable to save professional summary.');
      }

      setStatusMessage(result?.data?.message || 'Professional summary saved successfully.');
      setStatusType('success');
      return true;
    } catch (error) {
      console.error('[UI] handleSaveProfessionalSummary error:', error);
      setStatusMessage(error?.message || 'Unable to save professional summary.');
      setStatusType('error');
      return false;
    }
  };

  const openSummaryModal = () => setIsSummaryModalOpen(true);
  const closeSummaryModal = () => setIsSummaryModalOpen(false);

  const modalOnEnhance = async (currentText) => {
    try {
      const apiSection = mapClientToServerSection('professionalSummary');
      const result = await enhanceResumeSection({ section: apiSection, sectionText: currentText, jobDescription: jobDescription.trim() });
      if (!result?.success) return null;
      const data = result.data || {};
      return data.enhancedText || data.raw || null;
    } catch (err) {
      console.error('modalOnEnhance error', err);
      return null;
    }
  };

  const modalOnSave = async (text) => {
    try {
      const payload = {
        candidateId: candidateId ? Number(candidateId) : null,
        resumeId: resumeId ? Number(resumeId) : null,
        professionalSummary: text,
      };
      setFormData((prev) => ({ ...prev, professionalSummary: text }));
      const result = await saveProfessionalSummary(payload);
      if (!result?.success) {
        setStatusMessage(result?.message || 'Failed to save professional summary.');
        setStatusType('error');
      } else {
        setStatusMessage(result?.data?.message || 'Professional summary saved successfully.');
        setStatusType('success');
      }
    } catch (err) {
      console.error('modalOnSave error', err);
      setStatusMessage('Failed to save professional summary.');
      setStatusType('error');
    } finally {
      closeSummaryModal();
    }
  };

  const openTechModal = (index) => {
    setEditingTechIndex(index);
    setIsTechModalOpen(true);
  };
  const closeTechModal = () => {
    setEditingTechIndex(null);
    setIsTechModalOpen(false);
  };

  const techModalOnEnhance = async (currentDetails) => {
    try {
      const apiSection = mapClientToServerSection('technicalSkills');
      const result = await enhanceResumeSection({ section: apiSection, sectionText: currentDetails, jobDescription: jobDescription.trim() });
      if (!result?.success) return null;
      const data = result.data || {};
      return data.enhancedText || data.raw || null;
    } catch (err) {
      console.error('techModalOnEnhance error', err);
      return null;
    }
  };

  const techModalOnSave = async ({ title, details }) => {
    const idx = editingTechIndex;
    const nextSections = typeof idx === 'number' && idx >= 0 && idx < technicalSections.length
      ? technicalSections.map((s, i) => i === idx ? { title: title || '', details: details || '' } : s)
      : technicalSections;

    const normalizedNextSections = nextSections.map((section) => ({
      title: section.title || '',
      details: section.details || '',
    }));

    setTechnicalSections(normalizedNextSections);

    const payload = normalizedNextSections
      .filter((section) => (section.title || section.details || '').trim())
      .map((section) => ({
        title: section.title || 'Technical Skills',
        skills: String(section.details || '')
          .split(/\r?\n|\||,|;|•|\u2022|\u25E6|\u2043/)
          .map((skill) => skill.replace(/^[\-\*\s]+|[\-\*\s]+$/g, '').trim())
          .filter(Boolean),
      }))
      .filter((section) => section.skills.length > 0);

    console.log('[UI] Technical skills modal save payload:', {
      candidateId,
      resumeId,
      payload,
      editingTechIndex: idx,
      nextSections: normalizedNextSections,
    });

    if (!payload.length) {
      setStatusMessage('Add at least one technical skill before saving.');
      setStatusType('error');
      closeTechModal();
      return;
    }

    try {
      const apiPayload = {
        candidateId: candidateId ? Number(candidateId) : null,
        resumeId: resumeId ? Number(resumeId) : null,
        technicalSkills: payload,
      };

      const result = await saveTechnicalSkills(apiPayload);
      console.log('[UI] Technical skills modal save result:', JSON.stringify(result, null, 2));

      if (!result?.success) {
        throw new Error(result?.message || 'Unable to save technical skills.');
      }

      setStatusMessage(result?.data?.message || 'Technical skills saved successfully.');
      setStatusType('success');
    } catch (error) {
      console.error('[UI] techModalOnSave error:', error);
      setStatusMessage(error?.message || 'Unable to save technical skills.');
      setStatusType('error');
    } finally {
      closeTechModal();
    }
  };

  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [editingProjectIndex, setEditingProjectIndex] = useState(null);
  const [isWorkModalOpen, setIsWorkModalOpen] = useState(false);
  const [editingWorkIndex, setEditingWorkIndex] = useState(null);

  const openProjectModal = (index) => {
    setEditingProjectIndex(index);
    setIsProjectModalOpen(true);
  };
  const closeProjectModal = () => {
    setEditingProjectIndex(null);
    setIsProjectModalOpen(false);
  };

  const openWorkModal = (index) => {
    setEditingWorkIndex(index);
    setIsWorkModalOpen(true);
  };
  const closeWorkModal = () => {
    setEditingWorkIndex(null);
    setIsWorkModalOpen(false);
  };

  const projectModalOnEnhance = async (currentDetails) => {
    try {
      const apiSection = mapClientToServerSection('projectExperience');
      const result = await enhanceResumeSection({ section: apiSection, sectionText: currentDetails, jobDescription: jobDescription.trim() });
      if (!result?.success) return null;
      const data = result.data || {};
      return data.enhancedText || data.raw || null;
    } catch (err) {
      console.error('projectModalOnEnhance error', err);
      return null;
    }
  };

  const projectModalOnSave = ({ projectName, startDate, endDate, details }) => {
    const idx = editingProjectIndex;
    if (typeof idx === 'number' && idx >= 0 && idx < projectEntries.length) {
      setProjectEntries((prev) => prev.map((p, i) => i === idx ? { projectName: projectName || '', startDate, endDate, details } : p));
    }
    closeProjectModal();
  };

  const workModalOnEnhance = async (currentDetails) => {
    try {
      const apiSection = mapClientToServerSection('workExperience');
      const result = await enhanceResumeSection({ section: apiSection, sectionText: currentDetails, jobDescription: jobDescription.trim() });
      if (!result?.success) return null;
      const data = result.data || {};
      return data.enhancedText || data.raw || null;
    } catch (err) {
      console.error('workModalOnEnhance error', err);
      return null;
    }
  };

  const workModalOnSave = ({ company, jobTitle, startDate, endDate, details }) => {
    const idx = editingWorkIndex;
    if (typeof idx === 'number' && idx >= 0 && idx < workExperiences.length) {
      setWorkExperiences((prev) => prev.map((w, i) => i === idx ? { company: company || '', jobTitle: jobTitle || '', startDate, endDate, details } : w));
    }
    closeWorkModal();
  };

  const handleSaveTechnicalSkills = async () => {
    const payload = technicalSections
      .filter((section) => (section.title || section.details || '').trim())
      .map((section) => ({
        title: section.title || 'Technical Skills',
        skills: String(section.details || '')
          .split(/\r?\n|\||,|;|•|\u2022|\u25E6|\u2043/)
          .map((skill) => skill.replace(/^[\-\*\s]+|[\-\*\s]+$/g, '').trim())
          .filter(Boolean),
      }))
      .filter((section) => section.skills.length > 0);

    console.log('[UI] handleSaveTechnicalSkills clicked:', {
      candidateId,
      resumeId,
      sections: payload,
    });

    if (!payload.length) {
      setStatusMessage('Add at least one technical skill before saving.');
      setStatusType('error');
      return false;
    }

    if (!candidateId && !resumeId) {
      setStatusMessage('Enter a Candidate ID or Resume ID before saving the technical skills.');
      setStatusType('error');
      return false;
    }

    try {
      setStatusMessage('Saving technical skills...');
      setStatusType('info');
      const apiPayload = {
        candidateId: candidateId ? Number(candidateId) : null,
        resumeId: resumeId ? Number(resumeId) : null,
        technicalSkills: payload,
      };
      console.log('[UI] Technical skills payload to API:', JSON.stringify(apiPayload, null, 2));

      const result = await saveTechnicalSkills(apiPayload);
      console.log('[UI] Technical skills save result:', JSON.stringify(result, null, 2));

      if (!result?.success) {
        throw new Error(result?.message || 'Unable to save technical skills.');
      }

      setStatusMessage(result?.data?.message || 'Technical skills saved successfully.');
      setStatusType('success');
      return true;
    } catch (error) {
      console.error('[UI] handleSaveTechnicalSkills error:', error);
      setStatusMessage(error?.message || 'Unable to save technical skills.');
      setStatusType('error');
      return false;
    }
  };

  const handleSaveWorkExperience = async () => {
    const payload = workExperiences
      .filter((w) => (w.company || w.jobTitle || w.details || '').trim())
      .map((w) => ({
        company: w.company || '',
        jobTitle: w.jobTitle || '',
        role: w.jobTitle || '',
        startDate: w.startDate || '',
        endDate: w.endDate || '',
        description: w.details || '',
        details: w.details || '',
        responsibilities: w.details ? w.details.split(/\r?\n/) : [],
      }));

    if (!payload.length) {
      setStatusMessage('Add at least one work experience entry before saving.');
      setStatusType('error');
      return false;
    }

    if (!candidateId && !resumeId) {
      setStatusMessage('Enter a Candidate ID or Resume ID before saving the work experience.');
      setStatusType('error');
      return false;
    }

    try {
      setStatusMessage('Saving work experience...');
      setStatusType('info');
      const result = await saveWorkExperience({
        candidateId: candidateId ? Number(candidateId) : null,
        resumeId: resumeId ? Number(resumeId) : null,
        workExperience: payload,
      });

      if (!result?.success) {
        throw new Error(result?.message || 'Unable to save work experience.');
      }

      setStatusMessage(result?.data?.message || 'Work experience saved successfully.');
      setStatusType('success');
      return true;
    } catch (error) {
      console.error('[UI] handleSaveWorkExperience error:', error);
      setStatusMessage(error?.message || 'Unable to save work experience.');
      setStatusType('error');
      return false;
    }
  };

  const handleSaveProjectExperience = async () => {
    const payload = projectEntries
      .filter((project) => (project.projectName || project.details || '').trim())
      .map((project) => ({
        title: project.projectName || 'Project',
        startDate: project.startDate || '',
        endDate: project.endDate || '',
        description: project.details || '',
        details: project.details || '',
      }));

    console.log('[UI] handleSaveProjectExperience clicked:', {
      candidateId,
      resumeId,
      projectCount: payload.length,
      payload,
    });

    if (!payload.length) {
      setStatusMessage('Add at least one project before saving.');
      setStatusType('error');
      return false;
    }

    if (!candidateId && !resumeId) {
      setStatusMessage('Enter a Candidate ID or Resume ID before saving the project experience.');
      setStatusType('error');
      return false;
    }

    try {
      setStatusMessage('Saving project experience...');
      setStatusType('info');
      const apiPayload = {
        candidateId: candidateId ? Number(candidateId) : null,
        resumeId: resumeId ? Number(resumeId) : null,
        projectExperience: payload,
      };
      console.log('[UI] Project experience payload to API:', JSON.stringify(apiPayload, null, 2));

      const result = await saveProjectExperience(apiPayload);
      console.log('[UI] Project experience save result:', JSON.stringify(result, null, 2));

      if (!result?.success) {
        throw new Error(result?.message || 'Unable to save project experience.');
      }

      setStatusMessage(result?.data?.message || 'Project experience saved successfully.');
      setStatusType('success');
      return true;
    } catch (error) {
      console.error('[UI] handleSaveProjectExperience error:', error);
      setStatusMessage(error?.message || 'Unable to save project experience.');
      setStatusType('error');
      return false;
    }
  };

  const handleSaveEducation = async () => {
    const payload = educationEntries
      .filter((entry) => (entry.degree || entry.university || entry.grade || '').trim())
      .map((entry) => ({
        degree: entry.degree || '',
        institution: entry.university || '',
        university: entry.university || '',
        startDate: entry.startDate || '',
        endDate: entry.endDate || '',
        grade: entry.grade || '',
      }));

    if (!payload.length) {
      setStatusMessage('Add at least one education entry before saving.');
      setStatusType('error');
      return false;
    }

    if (!candidateId && !resumeId) {
      setStatusMessage('Enter a Candidate ID or Resume ID before saving education.');
      setStatusType('error');
      return false;
    }

    try {
      setStatusMessage('Saving education...');
      setStatusType('info');
      const result = await saveEducation({
        candidateId: candidateId ? Number(candidateId) : null,
        resumeId: resumeId ? Number(resumeId) : null,
        education: payload,
      });

      if (!result?.success) {
        throw new Error(result?.message || 'Unable to save education.');
      }

      setStatusMessage(result?.data?.message || 'Education saved successfully.');
      setStatusType('success');
      return true;
    } catch (error) {
      console.error('[UI] handleSaveEducation error:', error);
      setStatusMessage(error?.message || 'Unable to save education.');
      setStatusType('error');
      return false;
    }
  };

  const handleSaveLanguages = async () => {
    const payload = (languagesText || '').trim();

    if (!payload) {
      setStatusMessage('Add at least one language before saving.');
      setStatusType('error');
      return false;
    }

    if (!candidateId && !resumeId) {
      setStatusMessage('Enter a Candidate ID or Resume ID before saving languages.');
      setStatusType('error');
      return false;
    }

    try {
      setStatusMessage('Saving languages...');
      setStatusType('info');
      const result = await saveLanguages({
        candidateId: candidateId ? Number(candidateId) : null,
        resumeId: resumeId ? Number(resumeId) : null,
        languages: payload,
      });

      if (!result?.success) {
        throw new Error(result?.message || 'Unable to save languages.');
      }

      setStatusMessage(result?.data?.message || 'Languages saved successfully.');
      setStatusType('success');
      return true;
    } catch (error) {
      console.error('[UI] handleSaveLanguages error:', error);
      setStatusMessage(error?.message || 'Unable to save languages.');
      setStatusType('error');
      return false;
    }
  };

  const handleSaveCertifications = async () => {
      const payload = certificationEntries
        .filter((c) => (c.name || c.issuer || c.details || '').trim())
        .map((c, idx) => ({
          title: c.name || '',
          issuer: c.issuer || '',
          issueDate: c.startDate || null,
          expiryDate: c.endDate || null,
          description: c.details || '',
          sortOrder: idx + 1,
        }));

      console.log('[UI] handleSaveCertifications clicked:', { candidateId, resumeId, certCount: payload.length });

      if (!payload.length) {
        setStatusMessage('Add at least one certification before saving.');
        setStatusType('error');
        return false;
      }

      if (!candidateId && !resumeId) {
        setStatusMessage('Enter a Candidate ID or Resume ID before saving certifications.');
        setStatusType('error');
        return false;
      }

      try {
        setStatusMessage('Saving certifications...');
        setStatusType('info');
        const apiPayload = {
          candidateId: candidateId ? Number(candidateId) : null,
          resumeId: resumeId ? Number(resumeId) : null,
          certifications: payload,
        };
        console.log('[UI] Certifications payload to API:', JSON.stringify(apiPayload, null, 2));

        const result = await saveCertifications(apiPayload);
        console.log('[UI] Certifications save result:', JSON.stringify(result, null, 2));

        if (!result?.success) {
          throw new Error(result?.message || 'Unable to save certifications.');
        }

        setStatusMessage(result?.data?.message || 'Certifications saved successfully.');
        setStatusType('success');
        return true;
      } catch (error) {
        console.error('[UI] handleSaveCertifications error:', error);
        setStatusMessage(error?.message || 'Unable to save certifications.');
        setStatusType('error');
        return false;
      }
    };

    const handleSaveAchievements = async () => {
      const payload = achievementEntries
        .filter((c) => (c.name || c.issuer || c.details || '').trim())
        .map((c, idx) => ({
          title: c.name || '',
          issuer: c.issuer || '',
          issueDate: c.startDate || null,
          expiryDate: c.endDate || null,
          description: c.details || '',
          sortOrder: idx + 1,
        }));

      console.log('[UI] handleSaveAchievements clicked:', { candidateId, resumeId, achCount: payload.length });

      if (!payload.length) {
        setStatusMessage('Add at least one achievement before saving.');
        setStatusType('error');
        return false;
      }

      if (!candidateId && !resumeId) {
        setStatusMessage('Enter a Candidate ID or Resume ID before saving achievements.');
        setStatusType('error');
        return false;
      }

      try {
        setStatusMessage('Saving achievements...');
        setStatusType('info');
        const apiPayload = {
          candidateId: candidateId ? Number(candidateId) : null,
          resumeId: resumeId ? Number(resumeId) : null,
          achievements: payload,
        };
        console.log('[UI] Achievements payload to API:', JSON.stringify(apiPayload, null, 2));

        const result = await saveAchievements(apiPayload);
        console.log('[UI] Achievements save result:', JSON.stringify(result, null, 2));

        if (!result?.success) {
          throw new Error(result?.message || 'Unable to save achievements.');
        }

        setStatusMessage(result?.data?.message || 'Achievements saved successfully.');
        setStatusType('success');
        return true;
      } catch (error) {
        console.error('[UI] handleSaveAchievements error:', error);
        setStatusMessage(error?.message || 'Unable to save achievements.');
        setStatusType('error');
        return false;
      }
    };

  const getSectionText = (sectionKey) => {
    switch (sectionKey) {
      case 'professionalSummary':
        return formData.professionalSummary;
      case 'technicalSkills':
        if (technicalSections.length === 1 && !technicalSections[0].title) {
          return technicalSections[0].details;
        }
        return technicalSections.map((section) => `${section.title}: ${section.details}`).join('\n');
      case 'workExperience':
        return workExperiences.map((work) => `${work.company} | ${work.jobTitle} | ${work.startDate}-${work.endDate}\n${work.details}`).join('\n\n');
      case 'projectExperience':
        return projectEntries.map((project) => `${project.projectName} | ${project.startDate}-${project.endDate}\n${project.details}`).join('\n\n');
      case 'education':
        return educationEntries.map((education) => `${education.degree} | ${education.university} | ${education.grade || 'Grade'} | ${education.startDate}-${education.endDate}`).join('\n');
      case 'languages':
        return languagesText;
      case 'certifications':
        return certificationEntries.map((item) => `${item.name} | ${item.issuer} | ${item.startDate}-${item.endDate}\n${item.details}`).join('\n\n');
      case 'achievements':
        return achievementEntries.map((item) => `${item.name} | ${item.issuer} | ${item.startDate}-${item.endDate}\n${item.details}`).join('\n\n');
      default:
        return '';
    }
  };

  const setSectionText = (sectionKey, text) => {
    switch (sectionKey) {
      case 'professionalSummary':
        setFormData((prev) => ({ ...prev, professionalSummary: sanitizeProfessionalSummaryText(text) }));
        break;
      case 'technicalSkills':
        {
          const parsed = parseTechnicalSkillsText(text);
          if (parsed && parsed.length > 0) {
            setTechnicalSections(parsed);
            break;
          }
          setTechnicalSections([{ title: '', details: text }]);
        }
        break;
      case 'workExperience': {
        const parsed = parseWorkExperienceText(text);
        if (parsed && parsed.length > 0) {
          setWorkExperiences(parsed);
          break;
        }
        setWorkExperiences([{ company: '', jobTitle: '', startDate: '', endDate: '', details: text }]);
        break;
      }
      case 'projectExperience': {
        const parsedProjectEntries = parseProjectExperienceText(text);
        if (parsedProjectEntries.length > 0) {
          setProjectEntries(parsedProjectEntries);
          return;
        }
        setProjectEntries([{ projectName: '', startDate: '', endDate: '', details: text }]);
        break;
      }
      case 'education': {
        const parsed = parseEducationText(text);
        if (parsed && parsed.length > 0) {
          setEducationEntries(parsed);
          return;
        }
        setEducationEntries([{ degree: '', university: '', grade: '', startDate: '', endDate: '' }]);
        break;
      }
      case 'languages':
        setLanguagesText(text);
        break;
      case 'certifications':
        setCertificationEntries([{ name: text, issuer: '', startDate: '', endDate: '', details: '' }]);
        break;
      case 'achievements':
        setAchievementEntries([{ name: text, issuer: '', startDate: '', endDate: '', details: '' }]);
        break;
      default:
        break;
    }
  };

  const applyStructuredSection = (sectionKey, sectionData) => {
    if (!sectionData) return;
    setSectionStructured((prev) => ({ ...prev, [sectionKey]: sectionData }));
    if (Array.isArray(sectionData.items) && sectionData.items.length > 0) {
      const items = sectionData.items;
      switch (sectionKey) {
        case 'technicalSkills':
          {
            const sectionText = sectionData.enhancedText || sectionData.raw || '';
            const parsed = parseTechnicalSkillsText(sectionText);
            if (parsed && parsed.length > 0) {
              setTechnicalSections(parsed);
              return;
            }
          }
          setTechnicalSections(items.map((item) => ({ title: item.title || '', details: Array.isArray(item.details) ? item.details.join('\n') : item.details || '' })));
          return;
        case 'workExperience':
          {
            const sectionText = sectionData.enhancedText || sectionData.raw || '';
            const parsed = parseWorkExperienceText(sectionText);
            if (parsed && parsed.length > 0) {
              setWorkExperiences(parsed);
              return;
            }
          }
          setWorkExperiences(items.map((item) => ({
            company: item.company || item.organization || '',
            jobTitle: item.title || item.jobTitle || '',
            startDate: item.startDate || '',
            endDate: item.endDate || '',
            details: Array.isArray(item.bullets) ? item.bullets.join('\n') : item.details || item.highlights?.join('\n') || ''
          })));
          return;
        case 'projectExperience': {
          const sectionText = sectionData.enhancedText || sectionData.raw || '';
          const parsedProjectEntries = parseProjectExperienceText(sectionText);
          if (parsedProjectEntries.length > 0) {
            setProjectEntries(parsedProjectEntries);
            return;
          }

          setProjectEntries(items.map((item) => ({
            projectName: item.name || item.projectName || '',
            startDate: item.startDate || '',
            endDate: item.endDate || '',
            details: Array.isArray(item.bullets) ? item.bullets.join('\n') : item.details || ''
          })));
          return;
        }
        case 'education': {
          const parsed = parseEducationText(sectionData.enhancedText || sectionData.raw || '');
          if (parsed && parsed.length > 0) {
            setEducationEntries(parsed);
            return;
          }

          const validEducationItems = items
            .map((item) => ({
              degree: item.degree || item.name || '',
              university: item.institution || item.university || '',
              grade: item.grade || item.cgpa || item.gpa || '',
              startDate: item.startDate || '',
              endDate: item.endDate || ''
            }))
            .filter((item) => {
              const degreeText = (item.degree || '').trim();
              const universityText = (item.university || '').trim();
              const gradeText = (item.grade || '').trim();
              const hasDate = Boolean(item.startDate || item.endDate);
              return Boolean(degreeText || universityText || gradeText || hasDate);
            });

          if (validEducationItems.length > 0) {
            setEducationEntries(validEducationItems);
            return;
          }

          setEducationEntries([{ degree: '', university: '', grade: '', startDate: '', endDate: '' }]);
          return;
        }
        case 'certifications':
          setCertificationEntries(items.map((item) => ({
            name: item.name || item.title || '',
            issuer: item.issuer || '',
            startDate: item.startDate || '',
            endDate: item.endDate || '',
            details: Array.isArray(item.details) ? item.details.join('\n') : item.details || ''
          })));
          return;
        case 'achievements':
          setAchievementEntries(items.map((item) => ({
            name: item.name || item.title || '',
            issuer: item.issuer || '',
            startDate: item.startDate || '',
            endDate: item.endDate || '',
            details: Array.isArray(item.details) ? item.details.join('\n') : item.details || ''
          })));
          return;
        default:
          break;
      }
    }
    const text = sectionData.enhancedText || sectionData.raw || '';
    if (text) {
      setSectionText(sectionKey, text);
    }
  };

  // removed manual Groq key validation — server-side key is used automatically

  const handleExtractAllSections = async (file = selectedFile) => {
    if (!file) {
      setStatusMessage('Please upload a resume first.');
      setStatusType('error');
      return;
    }

    setIsBulkExtracting(true);
    setStatusMessage('');

    const sectionOrder = [
      'professionalSummary',
      'technicalSkills',
      'workExperience',
      'projectExperience',
      'education',
      'languages',
      'certifications'
    ];

    console.log('UI: handleExtractAllSections starting', { fileName: file?.name, jobDescriptionLength: jobDescription?.length });

    try {
      for (let index = 0; index < sectionOrder.length; index += 1) {
        const sectionKey = sectionOrder[index];
        const apiSection = mapClientToServerSection(sectionKey);
        setProgressMessage(`Extracting ${sectionTitles[sectionKey]} (${index + 1}/${sectionOrder.length})...`);

        const result = await extractResumeSection({
          file,
          section: apiSection,
          jobDescription: jobDescription.trim(),
        });

        if (!result.success) {
          setStatusMessage(result.message || `Failed to extract ${sectionTitles[sectionKey]}.`);
          setStatusType('error');
          setIsBulkExtracting(false);
          setProgressMessage('');
          return;
        }

        const sectionData = result.data || {};
        const structured = sectionData.structured || null;
        const extractedText = sectionData.raw || sectionData.enhancedText || sectionData.text || '';

        if (structured) {
          applyStructuredSection(sectionKey, structured);
        } else if (extractedText) {
          setSectionText(sectionKey, extractedText);
        }

        if (index < sectionOrder.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      setStatusMessage('Resume data extracted successfully. You can review and enhance each section.');
      setStatusType('success');
    } catch (error) {
      console.error('UI: handleExtractAllSections error', error);
      setStatusMessage('Failed to extract resume sections. Please try again.');
      setStatusType('error');
    } finally {
      setIsBulkExtracting(false);
      setProgressMessage('');
    }
  };

  const handleEnhanceAllSections = async () => {
    const sectionsPayload = {
      professionalSummary: formData.professionalSummary,
      technicalSkills: technicalSections.map((section) => `${section.title ? `${section.title}: ` : ''}${section.details}`.trim()).join('\n'),
      workExperience: workExperiences.map((work) => `${work.company || 'Company'} | ${work.jobTitle || 'Role'} | ${work.startDate || ''}-${work.endDate || ''}\n${work.details || ''}`.trim()).join('\n\n'),
      projectExperience: projectEntries.map((project) => `${project.projectName || 'Project'} | ${project.startDate || ''}-${project.endDate || ''}\n${project.details || ''}`.trim()).join('\n\n'),
      education: educationEntries.map((education) => `${education.degree || 'Degree'} | ${education.university || 'University'} | ${education.grade || 'Grade'} | ${education.startDate || ''}-${education.endDate || ''}`).join('\n'),
      languages: languagesText,
      certifications: certificationEntries.map((entry) => `${entry.name || 'Certification'} | ${entry.issuer || ''} | ${entry.startDate || ''}-${entry.endDate || ''}\n${entry.details || ''}`.trim()).join('\n\n'),
      achievements: achievementEntries.map((entry) => `${entry.name || 'Achievement'} | ${entry.issuer || ''} | ${entry.startDate || ''}-${entry.endDate || ''}\n${entry.details || ''}`.trim()).join('\n\n'),
    };

    const result = await enhanceAllSections({
      sections: sectionsPayload,
      jobDescription: jobDescription.trim(),
    });

    if (!result.success) {
      setStatusMessage(result.message || 'Failed to enhance all sections.');
      setStatusType('error');
      return;
    }

    const sections = result.data?.sections || {};
    Object.entries(sections).forEach(([serverSectionKey, sectionData]) => {
      const clientSectionKeys = mapServerToClientSectionKeys(serverSectionKey);
      clientSectionKeys.forEach((clientKey) => {
        const structured = sectionData?.structured || null;
        const textValue = sectionData?.enhancedText || sectionData?.text || sectionData?.raw || '';
        if (structured) {
          applyStructuredSection(clientKey, structured);
        } else if (textValue) {
          setSectionText(clientKey, textValue);
        }
      });
    });

    setStatusMessage('All resume sections enhanced successfully.');
    setStatusType('success');
  };

  const handleExtractSection = async (sectionKey) => {
    if (!selectedFile) {
      setSectionErrors((prev) => ({ ...prev, [sectionKey]: 'Please upload a resume first.' }));
      return;
    }

    setSectionLoading((prev) => ({ ...prev, [sectionKey]: true }));
    setSectionErrors((prev) => ({ ...prev, [sectionKey]: '' }));
    setStatusMessage('');

    const apiSection = mapClientToServerSection(sectionKey);
    console.log('UI: handleExtractSection calling API', { sectionKey, apiSection, fileName: selectedFile?.name });
    const result = await extractResumeSection({
      file: selectedFile,
      section: apiSection,
      jobDescription: jobDescription.trim(),
    });
    console.log('UI: handleExtractSection result', { sectionKey, apiSection, result });

    setSectionLoading((prev) => ({ ...prev, [sectionKey]: false }));

    if (!result.success) {
      const detailText = result.details ? ` (${JSON.stringify(result.details)})` : '';
      setSectionErrors((prev) => ({ ...prev, [sectionKey]: result.message || 'Extraction failed.' }));
      setStatusMessage((result.message || 'Extraction failed.') + detailText);
      setStatusType('error');
      return;
    }

    setSectionText(sectionKey, result.data?.raw || '');
    setStatusMessage(`${sectionTitles[sectionKey]} extracted successfully.`);
    setStatusType('success');
  };

  const handleEnhanceSection = async (sectionKey) => {
    const currentText = getSectionText(sectionKey) || '';
    if (!currentText.trim()) {
      setSectionErrors((prev) => ({ ...prev, [sectionKey]: 'No text available to enhance.' }));
      return;
    }

    // allow server-side key fallback

    setSectionLoading((prev) => ({ ...prev, [sectionKey]: true }));
    setSectionErrors((prev) => ({ ...prev, [sectionKey]: '' }));
    setProgressMessage(`Enhancing ${sectionTitles[sectionKey]}...`);
    setStatusMessage('');

    const apiSection = mapClientToServerSection(sectionKey);
    console.log('UI: handleEnhanceSection calling API', { sectionKey, apiSection, textLength: currentText.length });
    const result = await enhanceResumeSection({
      section: apiSection,
      sectionText: currentText,
      jobDescription: jobDescription.trim(),
    });
    console.log('UI: handleEnhanceSection result', { sectionKey, apiSection, result });

    setSectionLoading((prev) => ({ ...prev, [sectionKey]: false }));
    setProgressMessage('');

    if (!result.success) {
      setSectionErrors((prev) => ({ ...prev, [sectionKey]: result.message || 'Enhancement failed.' }));
      setStatusMessage(result.message || 'Enhancement failed.');
      setStatusType('error');
      return;
    }

    const data = result.data || {};
    const enhancedText = data.enhancedText || data.raw || getSectionText(sectionKey);
    setSectionText(sectionKey, enhancedText);
    setSectionStructured((prev) => ({ ...prev, [sectionKey]: data }));
    setStatusMessage(`${sectionTitles[sectionKey]} enhanced successfully.`);
    setStatusType('success');
  };

  const addTechnicalSection = () => {
    setTechnicalSections((prev) => [...prev, { title: '', details: '' }]);
  };

  const updateTechnicalSection = (index, field, value) => {
    setTechnicalSections((prev) => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const removeTechnicalSection = (index) => {
    setTechnicalSections((prev) => prev.filter((_, i) => i !== index));
  };

  const addWorkExperience = () => {
    setWorkExperiences((prev) => [...prev, { company: '', jobTitle: '', startDate: '', endDate: '', details: '' }]);
  };

  const updateWorkExperience = (index, field, value) => {
    setWorkExperiences((prev) => prev.map((w, i) => i === index ? { ...w, [field]: value } : w));
  };

  const removeWorkExperience = (index) => {
    setWorkExperiences((prev) => prev.filter((_, i) => i !== index));
  };

  const addProjectEntry = () => {
    setProjectEntries((prev) => {
      const next = [...prev, { projectName: '', startDate: '', endDate: '', details: '' }];
      setTimeout(() => {
        setEditingProjectIndex(next.length - 1);
        setIsProjectModalOpen(true);
      }, 0);
      return next;
    });
  };

  const updateProjectEntry = (index, field, value) => {
    setProjectEntries((prev) => prev.map((project, i) => i === index ? { ...project, [field]: value } : project));
  };

  const removeProjectEntry = (index) => {
    setProjectEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const addEducationEntry = () => {
    setEducationEntries((prev) => [...prev, { degree: '', university: '', grade: '', startDate: '', endDate: '' }]);
  };

  const updateEducationEntry = (index, field, value) => {
    setEducationEntries((prev) => prev.map((education, i) => i === index ? { ...education, [field]: value } : education));
  };

  const removeEducationEntry = (index) => {
    setEducationEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const addCertificationEntry = () => {
    setCertificationEntries((prev) => [...prev, { name: '', issuer: '', startDate: '', endDate: '', details: '' }]);
  };

  const updateCertificationEntry = (index, field, value) => {
    setCertificationEntries((prev) => prev.map((entry, i) => i === index ? { ...entry, [field]: value } : entry));
  };

  const removeCertificationEntry = (index) => {
    setCertificationEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const addAchievementEntry = () => {
    setAchievementEntries((prev) => {
      const next = [...prev, { name: '', issuer: '', startDate: '', endDate: '', details: '' }];
      setTimeout(() => {
        setEditingAchievementIndex(next.length - 1);
        setIsAchievementModalOpen(true);
      }, 0);
      return next;
    });
  };

  const updateAchievementEntry = (index, field, value) => {
    setAchievementEntries((prev) => prev.map((entry, i) => i === index ? { ...entry, [field]: value } : entry));
  };

  const removeAchievementEntry = (index) => {
    setAchievementEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const saveAllResumeSections = async () => {
    const saveSteps = [
      { label: 'Professional summary', handler: handleSaveProfessionalSummary },
      { label: 'Technical skills', handler: handleSaveTechnicalSkills },
      { label: 'Work experience', handler: handleSaveWorkExperience },
      { label: 'Project experience', handler: handleSaveProjectExperience },
      { label: 'Education', handler: handleSaveEducation },
      { label: 'Languages', handler: handleSaveLanguages },
      { label: 'Certifications', handler: handleSaveCertifications },
      { label: 'Achievements', handler: handleSaveAchievements },
    ];

    for (const step of saveSteps) {
      const saved = await step.handler();
      if (!saved) {
        setStatusMessage(`Stopped saving resume data at ${step.label}.`);
        setStatusType('error');
        return false;
      }
    }

    setStatusMessage('All resume sections saved successfully.');
    setStatusType('success');
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!resumeUploaded) {
      setStatusMessage('Please upload a resume first.');
      setStatusType('error');
      return;
    }

    if (!candidateId && !resumeId) {
      setStatusMessage('Enter a Candidate ID or Resume ID before saving the resume data.');
      setStatusType('error');
      return;
    }

    setStatusMessage('Saving all resume sections...');
    setStatusType('info');
    await saveAllResumeSections();
  };

  const downloadStructuredJson = () => {
    const data = {
      professionalSummary: {
        text: formData.professionalSummary,
        structured: sectionStructured.professionalSummary || null,
      },
      technicalSkills: {
        items: technicalSections,
        structured: sectionStructured.technicalSkills || null,
      },
      workExperience: {
        items: workExperiences,
        structured: sectionStructured.workExperience || null,
      },
      projectExperience: {
        items: projectEntries,
        structured: sectionStructured.projectExperience || null,
      },
      education: {
        items: educationEntries,
        structured: sectionStructured.education || null,
      },
      languages: {
        text: languagesText,
        structured: sectionStructured.languages || null,
      },
      certifications: {
        items: certificationEntries,
        structured: sectionStructured.certifications || null,
      },
      achievements: {
        items: achievementEntries,
        structured: sectionStructured.achievements || null,
      }
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'updated_resume_sections.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const resumePdfSections = buildResumePdfSections({
    formData,
    technicalSections,
    workExperiences,
    projectEntries,
    educationEntries,
    languagesText,
    certificationEntries,
    achievementEntries,
  });

  const openPdfPreview = () => {
    if (!resumePdfSections.length) {
      setStatusMessage('Please extract or add at least one resume section before converting to PDF.');
      setStatusType('error');
      return;
    }

    navigate('/resume-pdf-preview', {
      state: {
        formData,
        technicalSections,
        workExperiences,
        projectEntries,
        educationEntries,
        languagesText,
        certificationEntries,
        achievementEntries,
        candidateId,
        resumeId,
      },
    });
  };

  const handleDownloadResumePdf = async () => {
    if (!pdfPreviewRef.current) {
      setStatusMessage('Resume PDF preview is not ready yet.');
      setStatusType('error');
      return;
    }

    try {
      setStatusMessage('Preparing resume PDF...');
      setStatusType('info');

      const element = pdfPreviewRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const imageData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let positionY = 0;
      let remainingHeight = imgHeight;

      pdf.addImage(imageData, 'PNG', margin, positionY + margin, imgWidth, imgHeight);

      while (remainingHeight > pageHeight - margin * 2) {
        remainingHeight -= pageHeight - margin * 2;
        positionY -= pageHeight - margin * 2;
        pdf.addPage();
        pdf.addImage(imageData, 'PNG', margin, positionY + margin, imgWidth, imgHeight);
      }

      const fileName = candidateId || resumeId ? `resume-${candidateId || resumeId}.pdf` : 'resume-draft.pdf';
      pdf.save(fileName);
      setStatusMessage('Resume PDF downloaded successfully.');
      setStatusType('success');
    } catch (error) {
      console.error('Resume PDF generation failed:', error);
      setStatusMessage('Unable to generate the resume PDF. Please try again.');
      setStatusType('error');
    }
  };

  const renderResumePdfPreview = () => {
    const generatedName = candidateId || resumeId ? `Candidate ${candidateId || resumeId}` : 'Candidate Resume';
    const contactInfo = [
      '+91 00000 00000',
      'your.email@example.com',
      'Pune, Maharashtra',
    ].filter(Boolean);

    return (
      <div
        ref={pdfPreviewRef}
        style={{
          width: '100%',
          maxWidth: '794px',
          minHeight: '1123px',
          margin: '0 auto',
          background: '#ffffff',
          color: '#111827',
          border: '1px solid #d1d5db',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
          padding: '42px 48px',
          boxSizing: 'border-box',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div style={{ marginBottom: '18px', borderBottom: '2px solid #d1d5db', paddingBottom: '12px' }}>
          <div style={{ fontSize: '30px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.2 }}>
            {generatedName}
          </div>
          <div style={{ fontSize: '12px', color: '#374151', marginTop: '10px', lineHeight: 1.6 }}>
            {contactInfo.join(' | ')}
          </div>
        </div>

        {resumePdfSections.map((section) => (
          <div key={section.title} style={{ marginBottom: '18px', pageBreakInside: 'avoid', whiteSpace: 'pre-wrap' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1f2937', marginBottom: '8px' }}>
              {section.title}
            </div>
            <div style={{ fontSize: '12px', lineHeight: '1.7', color: '#111827' }}>
              {section.content}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="resume-page-wrapper">
      <div className="resume-container">
        <div className="resume-header">
          <h1 className="resume-title">Resume Update</h1>
          <p className="resume-subtitle">
            Upload your resume to auto-extract sections (server-side GROQ key is used). Optionally provide a Groq key to override.
          </p>
        </div>

        {statusMessage && (
          <div className={`alert ${statusType === 'error' ? 'alert-error' : statusType === 'info' ? 'alert-info' : 'alert-success'}`}>
            <div className="alert-content">{statusMessage}</div>
          </div>
        )}

        <form className="resume-form" onSubmit={handleSubmit}>
          {/* Groq API key input removed — server-side key is used automatically. */}

          {/*
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <label className="form-label" htmlFor="jobDescription" style={{ marginBottom: 0 }}>Job Description</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <label htmlFor="jobDescriptionType" className="form-label" style={{ marginBottom: 0 }}>JD Type</label>
                <select
                  id="jobDescriptionType"
                  value={jobDescriptionType}
                  onChange={(e) => setJobDescriptionType(e.target.value)}
                  style={{ minWidth: 180, padding: '8px 10px', borderRadius: 8, border: '1px solid #d0d7de', background: '#fff' }}
                >
                  <option value="Full JD">Full JD</option>
                  <option value="Technical JD">Technical JD</option>
                  <option value="Senior JD">Senior JD</option>
                  <option value="Internship JD">Internship JD</option>
                </select>
                <button
                  type="button"
                  onClick={handleCheckJobDescription}
                  className="primary-btn"
                  style={{ minWidth: 110 }}
                >
                  Check JD
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              <label className="form-label" htmlFor="jdSelect">Select JD</label>
              <select
                id="jdSelect"
                value={selectedJdId}
                onChange={handleJdSelectionChange}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d0d7de', background: '#fff' }}
              >
                {[16098, 16099, 16100, 16101, 16102, 16103, 16104, 16105, 16106, 16107, 16108, 16109, 16110, 16111, 16112, 16113, 16114, 16115, 16116, 16117].map((id) => (
                  <option key={id} value={String(id)}>{id}</option>
                ))}
              </select>

              <label className="form-label" htmlFor="customJobDescription">Or enter custom JD</label>
              <input
                id="customJobDescription"
                type="text"
                value={customJobDescription}
                onChange={handleCustomJdChange}
                placeholder="16098, 16099, 16100 or any JD value"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d0d7de', background: '#fff' }}
              />
            </div>
          </div>
          */}

          <div className="form-group">
            <label htmlFor="resumeFile" className="form-label">Upload Resume</label>
            <div className="file-upload-area">
              <input
                id="resumeFile"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                className="file-input"
              />
              <div className="file-upload-label">
                <div className="file-icon">📄</div>
                <div className="file-text">
                  <p className="file-main">{selectedFile ? selectedFile.name : 'Click to upload your resume'}</p>
                  <p className="file-sub">PDF, DOC, DOCX · Max 5 MB</p>
                </div>
              </div>
            </div>
            {fileError && <div className="alert alert-error"><div className="alert-content">{fileError}</div></div>}
          </div>

          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label" htmlFor="jdSelect">Select JD</label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr 100px',
                gap: '8px 12px',
                padding: '8px 12px',
                border: '1px solid #d0d7de',
                borderRadius: 8,
                background: '#f8fafc',
                fontSize: 12,
                fontWeight: 700,
                color: '#374151',
                marginBottom: 8,
              }}
            >
              <span>Job ID</span>
              <span>Title</span>
              <span>Status</span>
            </div>
            <select
              id="jdSelect"
              value={selectedJdId}
              onChange={handleJdSelectionChange}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d0d7de', background: '#fff' }}
              disabled={jobOptionsLoading}
            >
              <option value="">{jobOptionsLoading ? 'Loading jobs...' : 'Select a job...'}</option>
              {jobOptions.map((job) => (
                <option key={job.job_id} value={String(job.job_id)}>
                  {`${job.job_id} | ${job.title || 'Untitled Job'} | ${job.status || 'Unknown'}`}
                </option>
              ))}
            </select>

            <label className="form-label" htmlFor="customJobDescription" style={{ marginTop: 12 }}>Or paste a custom JD</label>
            <textarea
              id="customJobDescription"
              value={customJobDescription}
              onChange={handleCustomJdChange}
              rows={5}
              placeholder="Paste the full JD or custom job requirements here..."
              style={{ width: '100%', minHeight: 120, padding: '10px 12px', borderRadius: 8, border: '1px solid #d0d7de', background: '#fff', resize: 'vertical' }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={handleCheckJobDescription} className="primary-btn" style={{ minWidth: 110 }}>
                Check JD
              </button>
            </div>
          </div>

          {/*
          <div className="form-group">
            <label className="form-label">Save / Update target</label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 }}>
                <label className="form-label" htmlFor="candidateId">Candidate ID</label>
                <input
                  id="candidateId"
                  type="number"
                  value={candidateId}
                  onChange={(e) => setCandidateId(e.target.value)}
                  className="section-input"
                  placeholder="Candidate ID"
                  style={{ minWidth: 160 }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 }}>
                <label className="form-label" htmlFor="resumeId">Resume ID</label>
                <input
                  id="resumeId"
                  type="number"
                  value={resumeId}
                  onChange={(e) => setResumeId(e.target.value)}
                  className="section-input"
                  placeholder="Resume ID"
                  style={{ minWidth: 160 }}
                />
              </div>
            </div>
          </div>

          <div className="form-actions" style={{ marginBottom: 18 }}>
            <button type="button" className="btn btn-secondary" onClick={handleExtractAllSections} disabled={!selectedFile || isBulkExtracting}>
              {isBulkExtracting ? 'Extracting...' : 'Extract All Sections'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={downloadStructuredJson}>Download Structured JSON</button>
          </div>
          */}

          {progressMessage && (
            <div className="alert alert-info" style={{ marginBottom: 12 }}>
              <div className="alert-content">{progressMessage}</div>
            </div>
          )}

          {!resumeUploaded && (
            <div className="alert alert-info">
              <div className="alert-content">Upload a resume to start extracting data. Server-side GROQ key will be used automatically.</div>
            </div>
          )}

          {resumeUploaded && (
            <>
              <div className="resume-section-card">
                <div className="section-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="section-card-title">Professional Summary</span>
                </div>
                <label className="form-label" htmlFor="professionalSummaryField">Professional Summary</label>
                <textarea
                  id="professionalSummaryField"
                  name="professionalSummary"
                  readOnly
                  className="form-textarea"
                  rows={5}
                  placeholder="Add a polished professional summary for your resume..."
                  value={formData.professionalSummary}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <button type="button" className="btn btn-secondary" onClick={openSummaryModal}>
                    Edit
                  </button>
                </div>
                {sectionErrors.professionalSummary && <p className="error-text">{sectionErrors.professionalSummary}</p>}
              </div>

              {isSummaryModalOpen && (
                <ProfessionalSummaryModal
                  isOpen={isSummaryModalOpen}
                  onClose={closeSummaryModal}
                  initialText={formData.professionalSummary}
                  onSave={modalOnSave}
                  onEnhance={modalOnEnhance}
                />
              )}

              <div className="resume-section-card">
                <div className="section-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span className="section-card-title">Technical Skills</span>
                </div>
                <div className="section-list">
                  {technicalSections.map((sec, idx) => (
                    <div key={idx} className="section-item">
                      <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>Section title</label>
                      <div className="section-item-header" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder="Section title (e.g. Programming Languages)"
                          value={sec.title}
                          readOnly
                          className="section-input"
                          style={{ flex: 1 }}
                        />
                        <button type="button" aria-label="Edit technical section" onClick={() => openTechModal(idx)} style={{ background: 'transparent', border: 'none', fontSize: 16, cursor: 'pointer', marginLeft: 6 }}>
                          ✏️
                        </button>
                        <button type="button" className="remove-section-btn" onClick={() => removeTechnicalSection(idx)}>×</button>
                      </div>
                      <label className="form-label" style={{ display: 'block', marginTop: 10, marginBottom: 6 }}>Skills details</label>
                      <textarea
                        placeholder="Enter the skills for this section, separated by commas or bullet points"
                        value={sec.details}
                        readOnly
                        className="form-textarea"
                        rows={3}
                      />
                    </div>
                  ))}
                </div>
                {sectionErrors.technicalSkills && <p className="error-text">{sectionErrors.technicalSkills}</p>}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" className="section-add-btn" onClick={addTechnicalSection}>+ Add skill section</button>
                </div>

                {isTechModalOpen && typeof editingTechIndex === 'number' && (
                  <TechnicalSkillsModal
                    isOpen={isTechModalOpen}
                    onClose={closeTechModal}
                    initialTitle={(technicalSections[editingTechIndex] || {}).title}
                    initialDetails={(technicalSections[editingTechIndex] || {}).details}
                    onSave={techModalOnSave}
                    onEnhance={techModalOnEnhance}
                  />
                )}
              </div>

              <div className="resume-section-card">
                <div className="section-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span className="section-card-title">Work Experience</span>
                </div>
                <div className="section-list">
                  {workExperiences.map((we, idx) => (
                    <div key={idx} className="section-item">
                      <div className="education-item-actions">
                        <button type="button" aria-label="Edit work experience" onClick={() => openWorkModal(idx)} className="education-edit-btn">✏️</button>
                        <button type="button" className="remove-section-btn" onClick={() => removeWorkExperience(idx)}>×</button>
                      </div>

                      <div className="education-entry-row">
                        <div className="education-field education-field-wide">
                          <label className="form-label">Company name</label>
                          <input type="text" placeholder="Company name" value={we.company} readOnly className="section-input truncate" />
                        </div>
                        <div className="education-field education-field-wide">
                          <label className="form-label">Job title</label>
                          <input type="text" placeholder="Job title" value={we.jobTitle} readOnly className="section-input truncate" />
                        </div>
                      </div>

                      <div className="education-date-row">
                        <div className="education-date-field">
                          <label className="form-label">Start date</label>
                          <input type="date" value={we.startDate} readOnly className="section-input-date" />
                        </div>
                        <div className="education-date-field">
                          <label className="form-label">End date</label>
                          <input type="date" value={we.endDate} readOnly className="section-input-date" />
                        </div>
                      </div>

                      <label className="form-label" style={{ marginTop: 12 }}>Responsibilities / details</label>
                      <textarea placeholder="Add responsibilities and achievements for this work entry." value={we.details} readOnly className="form-textarea" rows={4} />
                    </div>
                  ))}
                </div>
                {sectionErrors.workExperience && <p className="error-text">{sectionErrors.workExperience}</p>}

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" className="section-add-btn" onClick={addWorkExperience}>+ Add work entry</button>
                </div>

                {isWorkModalOpen && typeof editingWorkIndex === 'number' && (
                  <WorkExperienceModal
                    isOpen={isWorkModalOpen}
                    onClose={closeWorkModal}
                    initialCompany={(workExperiences[editingWorkIndex] || {}).company}
                    initialJobTitle={(workExperiences[editingWorkIndex] || {}).jobTitle}
                    initialStart={(workExperiences[editingWorkIndex] || {}).startDate}
                    initialEnd={(workExperiences[editingWorkIndex] || {}).endDate}
                    initialDetails={(workExperiences[editingWorkIndex] || {}).details}
                    onSave={(updated) => {
                      setWorkExperiences((prev) => prev.map((we, i) => i === editingWorkIndex ? { ...we, ...updated } : we));
                      closeWorkModal();
                    }}
                    onEnhance={workModalOnEnhance}
                  />
                )}
              </div>

              <div className="resume-section-card">
                <div className="section-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span className="section-card-title">Project Experience</span>
                </div>
                <div className="section-list">
                  {projectEntries.map((project, idx) => (
                    <div key={idx} className="section-item">
                      <div className="section-item-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <label className="form-label">Project name</label>
                          <input type="text" placeholder="Project name" value={project.projectName} readOnly className="section-input" style={{ width: '100%', minWidth: 120 }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 28 }}>
                          <button type="button" aria-label="Edit project" onClick={() => openProjectModal(idx)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' }}>
                            ✏️
                          </button>
                          <button type="button" className="remove-section-btn" onClick={() => removeProjectEntry(idx)} aria-label="Remove project">
                            ×
                          </button>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', marginTop: 8 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <label className="form-label">Start date</label>
                            <input type="date" value={project.startDate} readOnly className="section-input-date" />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <label className="form-label">End date</label>
                            <input type="date" value={project.endDate} readOnly className="section-input-date" />
                          </div>
                        </div>
                      </div>
                      <label className="form-label" style={{ marginTop: 10 }}>Project details</label>
                      <textarea placeholder="Add project details and key outcomes." value={project.details} readOnly className="form-textarea" rows={4} />
                    </div>
                  ))}
                </div>
                {sectionErrors.projectExperience && <p className="error-text">{sectionErrors.projectExperience}</p>}

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 8 }}>
                  {/*
                  <button type="button" className="btn btn-secondary" onClick={() => handleExtractSection('projectExperience')} disabled={sectionLoading.projectExperience || !selectedFile}>
                    {sectionLoading.projectExperience ? 'Loading...' : 'Extract'}
                  </button>
                  */}
                  <button type="button" className="section-add-btn" onClick={addProjectEntry}>+ Add project</button>
                </div>

                {isProjectModalOpen && typeof editingProjectIndex === 'number' && (
                  <ProjectExperienceModal
                    isOpen={isProjectModalOpen}
                    onClose={closeProjectModal}
                    initialName={(projectEntries[editingProjectIndex] || {}).projectName}
                    initialStart={(projectEntries[editingProjectIndex] || {}).startDate}
                    initialEnd={(projectEntries[editingProjectIndex] || {}).endDate}
                    initialDetails={(projectEntries[editingProjectIndex] || {}).details}
                    onSave={projectModalOnSave}
                    onEnhance={projectModalOnEnhance}
                  />
                )}
              </div>

              <div className="resume-section-card">
                <div className="section-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span className="section-card-title">Education</span>
                  {/* Extract removed per UI change; Add button moved to section bottom */}
                </div>
                <div className="section-list">
                  {educationEntries.map((education, idx) => (
                    <div key={idx} className="section-item">
                      <div className="education-item-actions">
                        <button type="button" aria-label="Edit education" onClick={() => { setEditingEducationIndex(idx); setIsEducationModalOpen(true); }} className="education-edit-btn">✏️</button>
                        <button type="button" className="remove-section-btn" onClick={() => removeEducationEntry(idx)}>×</button>
                      </div>

                      <div className="education-entry-row">
                        <div className="education-field education-field-wide">
                          <label className="form-label">Degree / Education name</label>
                          <input type="text" placeholder="Degree / Education name" value={education.degree} readOnly className="section-input truncate" />
                        </div>
                        <div className="education-field education-field-wide">
                          <label className="form-label">University name</label>
                          <input type="text" placeholder="University name" value={education.university} readOnly className="section-input truncate" />
                        </div>
                        <div className="education-field education-field-small">
                          <label className="form-label">Grade / CGPA</label>
                          <input type="text" placeholder="Grade" value={education.grade} readOnly className="section-input truncate" />
                        </div>
                      </div>

                      <div className="education-date-row">
                        <div className="education-date-field">
                          <label className="form-label">Start date</label>
                          <input type="date" value={education.startDate} readOnly className="section-input-date" />
                        </div>
                        <div className="education-date-field">
                          <label className="form-label">End date</label>
                          <input type="date" value={education.endDate} readOnly className="section-input-date" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {sectionErrors.education && <p className="error-text">{sectionErrors.education}</p>}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" className="section-add-btn" onClick={addEducationEntry}>+ Add education</button>
                </div>

                {isEducationModalOpen && typeof editingEducationIndex === 'number' && (
                  <EducationModal
                    isOpen={isEducationModalOpen}
                    onClose={() => { setIsEducationModalOpen(false); setEditingEducationIndex(null); }}
                    initialDegree={(educationEntries[editingEducationIndex] || {}).degree}
                    initialUniversity={(educationEntries[editingEducationIndex] || {}).university}
                    initialGrade={(educationEntries[editingEducationIndex] || {}).grade}
                    initialStart={(educationEntries[editingEducationIndex] || {}).startDate}
                    initialEnd={(educationEntries[editingEducationIndex] || {}).endDate}
                    initialDetails={(educationEntries[editingEducationIndex] || {}).details}
                    onSave={(updated) => {
                      setEducationEntries((prev) => prev.map((ed, i) => i === editingEducationIndex ? { ...ed, ...updated } : ed));
                      setIsEducationModalOpen(false);
                      setEditingEducationIndex(null);
                    }}
                    onEnhance={async (text) => {
                      try {
                        const enhanced = await enhanceResumeSection(mapClientToServerSection('education'), text);
                        return enhanced?.enhancedText || enhanced?.text || enhanced || '';
                      } catch (err) {
                        console.error('EducationModal enhance error', err);
                        return null;
                      }
                    }}
                  />
                )}
              </div>

              <div className="resume-section-card">
                <div className="section-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span className="section-card-title">Languages</span>
                </div>
                <div className="section-item">
                  <label className="form-label">Languages and proficiency</label>
                  <textarea value={languagesText} onChange={(e) => setLanguagesText(e.target.value)} className="form-textarea" rows={3} placeholder="List languages and proficiency levels, for example: English, Hindi, Telugu" />
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }} />
                {sectionErrors.languages && <p className="error-text">{sectionErrors.languages}</p>}
              </div>

              <div className="resume-section-card">
                <div className="section-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span className="section-card-title">Certifications</span>
                </div>
                <div className="section-list">
                  {certificationEntries.map((entry, idx) => (
                    <div key={idx} className="section-item">
                      <div className="certification-item-actions">
                        <button type="button" aria-label="Edit certification" onClick={() => { setEditingCertificationIndex(idx); setIsCertificationModalOpen(true); }} className="education-edit-btn">✏️</button>
                        <button type="button" className="remove-section-btn" onClick={() => removeCertificationEntry(idx)}>×</button>
                      </div>

                      <div className="certification-entry-row">
                        <div className="certification-field certification-field-wide">
                          <label className="form-label">Certification name</label>
                          <input type="text" placeholder="Certification name" value={entry.name} readOnly className="section-input truncate" />
                        </div>
                        <div className="certification-field certification-field-wide">
                          <label className="form-label">Issuer</label>
                          <input type="text" placeholder="Issuer" value={entry.issuer} readOnly className="section-input truncate" />
                        </div>
                      </div>

                      <div className="certification-date-row">
                        <div className="certification-date-field">
                          <label className="form-label">Start date</label>
                          <input type="date" value={entry.startDate} readOnly className="section-input-date" />
                        </div>
                        <div className="certification-date-field">
                          <label className="form-label">End date</label>
                          <input type="date" value={entry.endDate} readOnly className="section-input-date" />
                        </div>
                      </div>

                      <label className="form-label" style={{ marginTop: 12 }}>Details</label>
                      <textarea
                        rows={8}
                        className="form-textarea"
                        value={entry.details}
                        readOnly
                        placeholder="Add certification details or notes."
                        style={{ width: '100%' }}
                      />
                    </div>
                  ))}
                </div>
                {sectionErrors.certifications && <p className="error-text">{sectionErrors.certifications}</p>}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" className="section-add-btn" onClick={addCertificationEntry}>+ Add certification</button>
                </div>

                {isCertificationModalOpen && typeof editingCertificationIndex === 'number' && (
                  <CertificationModal
                    isOpen={isCertificationModalOpen}
                    onClose={() => { setIsCertificationModalOpen(false); setEditingCertificationIndex(null); }}
                    initialName={(certificationEntries[editingCertificationIndex] || {}).name}
                    initialIssuer={(certificationEntries[editingCertificationIndex] || {}).issuer}
                    initialStart={(certificationEntries[editingCertificationIndex] || {}).startDate}
                    initialEnd={(certificationEntries[editingCertificationIndex] || {}).endDate}
                    initialDetails={(certificationEntries[editingCertificationIndex] || {}).details}
                    onSave={(updated) => {
                      setCertificationEntries((prev) => prev.map((entry, i) => i === editingCertificationIndex ? { ...entry, ...updated } : entry));
                      setIsCertificationModalOpen(false);
                      setEditingCertificationIndex(null);
                    }}
                    onEnhance={async (text) => {
                      try {
                        const enhanced = await enhanceResumeSection(mapClientToServerSection('certifications'), text);
                        return enhanced?.enhancedText || enhanced?.text || enhanced || '';
                      } catch (err) {
                        console.error('CertificationModal enhance error', err);
                        return null;
                      }
                    }}
                  />
                )}
              </div>

              <div className="resume-section-card">
                <div className="section-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span className="section-card-title">Achievements</span>
                </div>
                <div className="section-list">
                  {achievementEntries.map((entry, idx) => (
                    <div key={idx} className="section-item">
                      <div className="certification-item-actions">
                        <button type="button" aria-label="Edit achievement" onClick={() => { setEditingAchievementIndex(idx); setIsAchievementModalOpen(true); }} className="education-edit-btn">✏️</button>
                        <button type="button" className="remove-section-btn" onClick={() => removeAchievementEntry(idx)}>×</button>
                      </div>

                      <div className="certification-entry-row">
                        <div className="certification-field certification-field-wide">
                          <label className="form-label">Achievement name</label>
                          <input type="text" placeholder="Achievement name" value={entry.name} readOnly className="section-input truncate" />
                        </div>
                        <div className="certification-field certification-field-wide">
                          <label className="form-label">Issued by</label>
                          <input type="text" placeholder="Issued by" value={entry.issuer} readOnly className="section-input truncate" />
                        </div>
                      </div>

                      <div className="certification-date-row">
                        <div className="certification-date-field">
                          <label className="form-label">Start date</label>
                          <input type="date" value={entry.startDate} readOnly className="section-input-date" />
                        </div>
                        <div className="certification-date-field">
                          <label className="form-label">End date</label>
                          <input type="date" value={entry.endDate} readOnly className="section-input-date" />
                        </div>
                      </div>

                      <label className="form-label" style={{ marginTop: 12 }}>Achievement details</label>
                      <textarea placeholder="Add achievement details or notes." value={entry.details} readOnly className="form-textarea" rows={3} />
                    </div>
                  ))}
                </div>
                {sectionErrors.achievements && <p className="error-text">{sectionErrors.achievements}</p>}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" className="section-add-btn" onClick={addAchievementEntry}>+ Add achievement</button>
                </div>

                {isAchievementModalOpen && typeof editingAchievementIndex === 'number' && (
                  <AchievementModal
                    isOpen={isAchievementModalOpen}
                    onClose={() => { setIsAchievementModalOpen(false); setEditingAchievementIndex(null); }}
                    initialName={(achievementEntries[editingAchievementIndex] || {}).name}
                    initialIssuer={(achievementEntries[editingAchievementIndex] || {}).issuer}
                    initialStart={(achievementEntries[editingAchievementIndex] || {}).startDate}
                    initialEnd={(achievementEntries[editingAchievementIndex] || {}).endDate}
                    initialDetails={(achievementEntries[editingAchievementIndex] || {}).details}
                    onSave={(updated) => {
                      setAchievementEntries((prev) => prev.map((entry, i) => i === editingAchievementIndex ? { ...entry, ...updated } : entry));
                      setIsAchievementModalOpen(false);
                      setEditingAchievementIndex(null);
                    }}
                    onEnhance={async (text) => {
                      try {
                        const enhanced = await enhanceResumeSection(mapClientToServerSection('achievements'), text);
                        return enhanced?.enhancedText || enhanced?.text || enhanced || '';
                      } catch (err) {
                        console.error('AchievementModal enhance error', err);
                        return null;
                      }
                    }}
                  />
                )}
              </div>

              <div className="resume-section-card" style={{ background: '#f8fafc' }}>
                <div className="section-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span className="section-card-title">Resume PDF Export</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                  <p style={{ margin: 0, color: '#4b5563' }}>
                    Collect all resume sections, organize them on a clean page, and open the final printable version in a separate PDF preview screen.
                  </p>
                  <button type="button" className="btn btn-primary" onClick={openPdfPreview}>Convert to PDF</button>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Update Resume Data</button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/candidates')}>Back to Candidates</button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default ResumeDataInsertPage;
